import React, { useCallback, useEffect, useMemo, useState } from "react";
import AuditTrailDrawer, {
	AuditEntry,
} from "../components/common/AuditTrailDrawer";
import DataTable from "../components/common/DataTable";
import { useAdminApi } from "../hooks/useAdminApi";
import { AuditLogEntry } from "../types/admin";

const AuditLogsPage: React.FC = () => {
	const api = useAdminApi();
	const [rows, setRows] = useState<AuditEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [keyword, setKeyword] = useState("");

	const mapLog = useCallback(
		(log: AuditLogEntry): AuditEntry => ({
			id: log.id,
			actor: log.admin_user?.name || log.admin_user?.email || "unknown",
			action: log.action,
			target: [log.target_type, log.target_id].filter(Boolean).join("#") || "-",
			before: log.before ? JSON.stringify(log.before) : null,
			after: log.after ? JSON.stringify(log.after) : null,
			created_at: log.created_at,
		}),
		[]
	);

	const fetchLogs = useCallback(
		async (pageOverride?: number) => {
			setLoading(true);
			setError(null);
			try {
				const res = await api.listAuditLogs({
					q: keyword || undefined,
					page: pageOverride ?? page,
					per_page: pageSize,
				});
				const mapped = res.data.map(mapLog);
				setRows(mapped);
				setTotal(res.total);
				setPageSize(res.per_page);
			} catch (err) {
				console.warn("Failed to load audit logs", err);
				setError("監査ログの取得に失敗しました");
			} finally {
				setLoading(false);
			}
		},
		[api, keyword, page, pageSize, mapLog]
	);

	useEffect(() => {
		fetchLogs();
	}, [fetchLogs]);

	const columns = useMemo(
		() => [
			{ key: "created_at", header: "日時" },
			{ key: "actor", header: "操作者" },
			{ key: "action", header: "操作" },
			{ key: "target", header: "対象" },
		],
		[]
	);

	const handleSearch = () => {
		setPage(1);
		fetchLogs(1);
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-ink">Audit Logs</h1>
					<p className="text-sm text-muted">
						全更新系 API の admin_activity_logs
					</p>
				</div>
				<button
					className="rounded-xl border border-surface-border px-3 sm:px-4 py-2 text-sm font-semibold text-primary"
					onClick={() => setDrawerOpen(true)}
				>
					詳細ドロワー
				</button>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="flex gap-2">
					<input
						className="input"
						placeholder="action / target"
						value={keyword}
						onChange={(e) => setKeyword(e.target.value)}
					/>
					<button
						className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
						onClick={handleSearch}
					>
						検索
					</button>
				</div>

				<div className="mt-4">
					<DataTable
						data={rows}
						columns={columns}
						total={total}
						page={page}
						pageSize={pageSize}
						onPageChange={(p) => {
							setPage(p);
							fetchLogs(p);
						}}
						loading={loading}
						error={error}
					/>
				</div>
			</div>

			<AuditTrailDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				entries={rows}
			/>
		</div>
	);
};

export default AuditLogsPage;
